import React, { useState, useEffect, useCallback } from 'react';
import TimesheetRow from '../../timesheets/components/TimesheetRow.js';
import _ from 'lodash';
import { requestTimesheets } from '../../../actions/TimesheetsActions.js';
import moment from 'moment';
import { connect } from 'react-redux';
import * as timesheetsSelectors from '../../../selectors/TimesheetsSelectors.js';
import NoEntriesFound from './../../components/NoEntriesFound.js';
import DetailsLoading from '../../components/DetailsLoading.js';

const UnsyncedTimeEntriesTable = ({
    entriesByWeek,
    weeklyEntriesByWeek,
    weekIds,
    duplicateTimeEntry,
    weeklyTotals,
    weeklyGrandTotal,
    qbType,
    activeTimerEntryId,
    requestTimesheets,
    loading,
}) => {
    useEffect(() => {
        requestTimesheets({
            showLoading: true,
            queryParams: {
                exported: 'No',
                approved: 'Yes',
                search_end_date: '',
                search_start_date: '',
            },
        });
    }, []);

    if (loading) return <DetailsLoading description="Loading Timesheets" />;

    const weeks = weekIds.map(timestamp => {
        const startOfWeek = moment(parseInt(timestamp, 10));
        const entries = entriesByWeek[timestamp].map(entry => {
            return { ...entry, ...{ start_of_the_week: startOfWeek.format('MMM D, YYYY') } };
        });
        const weeklyEntries = weeklyEntriesByWeek[timestamp].map(weeklyEntry => {
            return {
                ...weeklyEntry,
                ...{ start_of_the_week: startOfWeek.format('MMM D, YYYY') },
            };
        });

        return {
            id: timestamp,
            startOfWeek,
            label: startOfWeek.format('MMM D, YYYY'),
            entries: entries,
            weeklyEntries: weeklyEntries,
        };
    });

    if (weeks.length === 0) return <NoEntriesFound type="Timesheets" />;

    const renderWeekdayHeaders = (startOfWeek, totals) => {
        const startDate = moment(startOfWeek);
        return _.range(0, 7).map(index => {
            const day = startDate.format('ddd');
            startDate.add(1, 'd');
            const total = totals[day].toFixed(2);
            return (
                <th key={`weekday-${index}`} className="tsDetailsDayCell">
                    <span className="date">{day}</span>
                    <span className={total > 0 ? 'dayTotal' : 'emptyTotal'}>{total}</span>
                </th>
            );
        });
    };

    const renderWeekdayDatesAndTotals = (startOfWeek, totals) => {
        const startDate = moment(startOfWeek);

        return _.range(0, 7).map(index => {
            const date = startDate.format('MMM D');
            const day = startDate.format('ddd');
            startDate.add(1, 'd');
            const total = totals[day].toFixed(2);
            return (
                <td key={`weekday-${index}`} className="tsDetailsDayCell">
                    <span className="date">{date}</span>
                    <span className={total > 0 ? 'dayTotal' : 'emptyTotal'}>{total}</span>
                </td>
            );
        });
    };

    return (
        <table className="dataTable">
            <thead>
                <tr>
                    <th />
                    <th className="tsEmployeeCell">Employee</th>
                    <th className="tsCustomerJobDescriptionCell">Customer &amp; Job Details</th>
                    {renderWeekdayHeaders(weeks[0].startOfWeek, weeklyGrandTotal)}
                    <th className="tsTotalHours">
                        Total
                        <br />
                        {weeklyGrandTotal.sum.toFixed(2)} Hours
                    </th>
                </tr>
            </thead>
            {_.map(weeks, week => {
                return (
                    <tbody key={week.id}>
                        <tr className="tsWeekHeader">
                            <td />
                            <td />
                            <td>Week of {week.label}</td>
                            {renderWeekdayDatesAndTotals(week.startOfWeek, weeklyTotals[week.id])}
                            <td />
                        </tr>
                        {_.map(week.weeklyEntries, entry => (
                            <TimesheetRow
                                key={'uuid' in entry ? entry.uuid : entry.id}
                                entry={entry}
                                qbType={qbType}
                                startOfWeek={week.startOfWeek}
                                isContinuingTimerRow={
                                    activeTimerEntryId &&
                                    ('entriesInGroupById' in entry
                                        ? Object.keys(entry.entriesInGroupById).includes(
                                              activeTimerEntryId
                                          )
                                        : activeTimerEntryId === entry.id)
                                }
                                groupedByWeek
                                readOnly
                            />
                        ))}
                    </tbody>
                );
            })}
        </table>
    );
};

const mapStateToProps = state => {
    const { timerStore, accountStore, timesheetsStore } = state;

    return {
        weekIds: timesheetsSelectors.selectSortedWeekIds(state),
        entriesByWeek: timesheetsSelectors.selectGroupedByWeekAndSorted(state),
        weeklyEntriesByWeek: timesheetsSelectors.selectGroupedByWeekAndWeekHashAndEmployee(state),
        weeklyGrandTotal: timesheetsSelectors.selectWeeklyGrandTotal(state),
        qbType: accountStore.qb_type,
        weeklyTotals: timesheetsSelectors.selectWeeklyTotals(state),
        activeTimerEntryId: timerStore.timeEntryId,
        loading: timesheetsStore.loading,
    };
};

const mapDispatchToProps = {
    requestTimesheets,
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(UnsyncedTimeEntriesTable);
